import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import '../css/PickOneAutoTest.css';
import '../css/PickOneAutoTestEnhancements.css';

const STORAGE_KEY = 'admin_pick_one_auto_sandbox_v1';
const RANKS = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const INTENSITIES = ['LIGHT', 'MEDIUM', 'HEAVY', 'ANY'];
const INTENSITY_META = {
  LIGHT: { label: 'เบา', icon: '🟢', level: 0 },
  MEDIUM: { label: 'กลาง', icon: '🟡', level: 1 },
  HEAVY: { label: 'หนัก', icon: '🔴', level: 2 },
  ANY: { label: 'ได้ทุกระดับ', icon: '⚪', level: null }
};
const EMPTY_DATA = { sequence: 0, matchSequence: 0, players: [], queue: [], playing: [], history: [], pairs: [], logs: [] };

const readStorage = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return value && Array.isArray(value.players) ? { ...EMPTY_DATA, ...value } : EMPTY_DATA;
  } catch {
    return EMPTY_DATA;
  }
};

const intensityCompatible = players => {
  const levels = players.map(player => INTENSITY_META[player.intensity]?.level).filter(Number.isFinite);
  return levels.length < 2 || Math.max(...levels) - Math.min(...levels) <= 1;
};

const intensityPriority = (anchor, candidate) => {
  const anchorRankIndex = RANKS.indexOf(anchor.rank);
  const candidateRankIndex = RANKS.indexOf(candidate.rank);
  if (candidateRankIndex < anchorRankIndex) {
    if (candidate.intensity === 'HEAVY') return 0;
    if (candidate.intensity === 'MEDIUM' || candidate.intensity === 'ANY') return 1;
    return 2;
  }
  if (candidateRankIndex > anchorRankIndex) {
    if (candidate.intensity === 'MEDIUM') return 0;
    if (candidate.intensity === 'HEAVY' || candidate.intensity === 'ANY') return 1;
    return 2;
  }
  if (anchor.intensity === 'ANY') {
    if (candidate.intensity === 'MEDIUM') return 0;
    if (candidate.intensity === 'ANY') return 1;
    return 2;
  }
  if (candidate.intensity === anchor.intensity) return 0;
  if (candidate.intensity === 'ANY') return 1;
  return 2;
};

const pairKey = (left, right) => [String(left), String(right)].sort().join('::');
const respectsPairSelection = (players, pairs) => pairs.every(pair => {
  const count = [pair.userA, pair.userB].filter(id => players.some(player => player.id === id)).length;
  return count === 0 || count === 2;
});

const getMatchPlayers = match => [...(match?.team1 || []), ...(match?.team2 || [])];

const getLatestSandboxMatch = matches => (matches || []).reduce((latest, match) => (
  !latest || Number(match.matchNo || 0) > Number(latest.matchNo || 0) ? match : latest
), null);

const relationCount = (matches, userA, userB, relation) => (matches || []).reduce((count, match) => {
  const team1Ids = new Set((match.team1 || []).map(player => player.id));
  const team2Ids = new Set((match.team2 || []).map(player => player.id));
  const sameTeam = (team1Ids.has(userA) && team1Ids.has(userB)) || (team2Ids.has(userA) && team2Ids.has(userB));
  const opponents = (team1Ids.has(userA) && team2Ids.has(userB)) || (team2Ids.has(userA) && team1Ids.has(userB));
  return count + Number(relation === 'TEAMMATE' ? sameTeam : opponents);
}, 0);

const respectsPreviousMatchOverlap = (players, matches) => {
  const latestMatch = getLatestSandboxMatch(matches);
  if (!latestMatch) return true;
  const latestIds = new Set(getMatchPlayers(latestMatch).map(player => player.id));
  return players.filter(player => latestIds.has(player.id)).length <= 2;
};

const respectsPairMeetingLimit = (team1, team2, matches) => {
  const teammatePairs = [[team1[0], team1[1]], [team2[0], team2[1]]];
  if (teammatePairs.some(([left, right]) => relationCount(matches, left.id, right.id, 'TEAMMATE') >= 2)) return false;
  return team1.every(left => team2.every(right => relationCount(matches, left.id, right.id, 'OPPONENT') < 2));
};

const buildMatchAudits = (matches, protectedPairs = []) => {
  const orderedMatches = [...(matches || [])].sort((left, right) => Number(left.matchNo || 0) - Number(right.matchNo || 0));
  return orderedMatches.map((match, index) => {
    const priorMatches = orderedMatches.slice(0, index);
    const players = getMatchPlayers(match);
    const ranks = new Set(players.map(player => player.rank));
    const concreteIntensities = new Set(players.map(player => player.intensity).filter(value => value && value !== 'ANY'));
    const latestPrior = getLatestSandboxMatch(priorMatches);
    const latestPriorIds = new Set(getMatchPlayers(latestPrior).map(player => player.id));
    const previousOverlap = players.filter(player => latestPriorIds.has(player.id)).length;
    const teammatePairs = [[match.team1[0], match.team1[1]], [match.team2[0], match.team2[1]]];
    const opponentPairs = match.team1.flatMap(left => match.team2.map(right => [left, right]));
    const teammateDetails = teammatePairs.map(([left, right]) => ({
      label: `${left.name} + ${right.name}`,
      count: relationCount(priorMatches, left.id, right.id, 'TEAMMATE') + 1
    }));
    const opponentDetails = opponentPairs.map(([left, right]) => ({
      label: `${left.name} ↔ ${right.name}`,
      count: relationCount(priorMatches, left.id, right.id, 'OPPONENT') + 1
    }));
    const repeatPass = [...teammateDetails, ...opponentDetails].every(item => item.count <= 2);
    const protectedPairPass = protectedPairs.every(pair => {
      const selected = players.some(player => player.id === pair.userA) || players.some(player => player.id === pair.userB);
      if (!selected) return true;
      return [match.team1, match.team2].some(team => team.some(player => player.id === pair.userA) && team.some(player => player.id === pair.userB));
    });
    const checks = [
      { pass: ranks.size === 1, reason: 'ข้าม Rank เพราะผู้เล่น Rank เดียวกันไม่ครบ 4 คน' },
      { pass: concreteIntensities.size <= 1, reason: 'ใช้ความหนักระดับใกล้เคียง เพราะระดับเดียวกันมีผู้เล่นไม่ครบ' },
      { pass: !latestPrior || previousOverlap <= 2, reason: `ใช้ผู้เล่นจากเกมก่อนหน้าซ้ำ ${previousOverlap} คน (กำหนดไม่เกิน 2 คน)` },
      { pass: repeatPass, reason: 'มีคู่ทีมหรือคู่ตรงข้ามพบกันเกิน 2 รอบ' },
      { pass: protectedPairPass, reason: 'Waiting Pair/Locked Pair ไม่ได้อยู่ทีมเดียวกัน' }
    ];
    const passed = checks.filter(check => check.pass).length;
    return {
      ...match,
      percent: Math.round((passed / checks.length) * 100),
      reasons: checks.filter(check => !check.pass).map(check => check.reason),
      metrics: {
        sameRank: ranks.size === 1,
        sameIntensity: concreteIntensities.size <= 1,
        previousOverlapPass: !latestPrior || previousOverlap <= 2,
        repeatPass,
        protectedPairPass
      },
      previousOverlap,
      teammateDetails,
      opponentDetails
    };
  }).reverse();
};

const bestTeams = (players, pairs = [], matches = []) => {
  const layouts = [
    [[players[0], players[1]], [players[2], players[3]]],
    [[players[0], players[2]], [players[1], players[3]]],
    [[players[0], players[3]], [players[1], players[2]]]
  ];
  return layouts.filter(([team1, team2]) => {
    const respectsProtectedPairs = pairs.every(pair => {
      const selected = players.some(player => player.id === pair.userA) || players.some(player => player.id === pair.userB);
      if (!selected) return true;
      return [team1, team2].some(team => team.some(player => player.id === pair.userA) && team.some(player => player.id === pair.userB));
    });
    return respectsProtectedPairs && respectsPairMeetingLimit(team1, team2, matches);
  }).map(([team1, team2]) => ({
    team1,
    team2,
    gap: Math.abs(team1.reduce((sum, player) => sum + player.point, 0) - team2.reduce((sum, player) => sum + player.point, 0))
  })).sort((left, right) => left.gap - right.gap)[0] || null;
};

const buildAutoMatch = (activePlayers, requestedAnchor, pairs = [], matches = []) => {
  const anchor = requestedAnchor || [...activePlayers].sort((left, right) => left.games - right.games || left.id.localeCompare(right.id))[0];
  if (!anchor) return null;
  const candidates = activePlayers
    .filter(player => player.id !== anchor.id)
    .filter(player => intensityCompatible([anchor, player]))
    .sort((left, right) =>
      Number(left.rank !== anchor.rank) - Number(right.rank !== anchor.rank) ||
      Math.abs(RANKS.indexOf(left.rank) - RANKS.indexOf(anchor.rank)) - Math.abs(RANKS.indexOf(right.rank) - RANKS.indexOf(anchor.rank)) ||
      intensityPriority(anchor, left) - intensityPriority(anchor, right) ||
      Math.abs(left.point - anchor.point) - Math.abs(right.point - anchor.point) ||
      left.games - right.games
    );
  for (let first = 0; first < candidates.length; first += 1) {
    for (let second = first + 1; second < candidates.length; second += 1) {
      for (let third = second + 1; third < candidates.length; third += 1) {
        const group = [anchor, candidates[first], candidates[second], candidates[third]];
        if (!intensityCompatible(group) || !respectsPairSelection(group, pairs) || !respectsPreviousMatchOverlap(group, matches)) continue;
        const teams = bestTeams(group, pairs, matches);
        if (teams) return { ...teams, anchorId: anchor.id };
      }
    }
  }
  return null;
};

function PlayerChip({ player }) {
  const intensity = INTENSITY_META[player.intensity] || INTENSITY_META.ANY;
  return <span className="poat-player-chip">
    <b>{player.name}</b><small>Rank {player.rank} · {intensity.icon} {intensity.label} · {player.point} คะแนน</small>
  </span>;
}

function MatchTeams({ match }) {
  return <div className="poat-teams">
    <div><strong>Team 1</strong>{match.team1.map(player => <PlayerChip key={player.id} player={player} />)}</div>
    <b className="poat-vs">VS</b>
    <div><strong>Team 2</strong>{match.team2.map(player => <PlayerChip key={player.id} player={player} />)}</div>
  </div>;
}

function AuditTeam({ label, players }) {
  return <div className="poat-audit-team"><strong>{label}</strong>{players.map(player => {
    const intensity = INTENSITY_META[player.intensity] || INTENSITY_META.ANY;
    return <span key={player.id}><b>{player.name}</b><small>Rank {player.rank} · {intensity.icon} {intensity.label}</small></span>;
  })}</div>;
}

export default function PickOneAutoTest() {
  const navigate = useNavigate();
  const [actor] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [data, setData] = useState(readStorage);
  const [rank, setRank] = useState('N');
  const [intensity, setIntensity] = useState('MEDIUM');
  const [generateCount, setGenerateCount] = useState(10);
  const [anchorId, setAnchorId] = useState('AUTO');
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [autoAiEnabled, setAutoAiEnabled] = useState(false);
  const [pairA, setPairA] = useState('');
  const [pairB, setPairB] = useState('');
  const [pairType, setPairType] = useState('WAITING');
  const [logPlayerId, setLogPlayerId] = useState('ALL');

  useEffect(() => {
    if (!actor || String(actor.role).toLowerCase() !== 'admin') navigate('/', { replace: true });
  }, [actor, navigate]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const activePlayers = useMemo(() => data.players.filter(player => player.status === 'ACTIVE'), [data.players]);
  const sandboxMatches = useMemo(() => [...data.history, ...data.queue, ...data.playing], [data.history, data.queue, data.playing]);
  const matchAudits = useMemo(() => buildMatchAudits(sandboxMatches, data.pairs || []), [sandboxMatches, data.pairs]);
  const auditPercent = matchAudits.length
    ? Math.round(matchAudits.reduce((sum, match) => sum + match.percent, 0) / matchAudits.length)
    : 0;
  const overallReport = useMemo(() => {
    const total = matchAudits.length;
    const percent = count => total ? Math.round((count / total) * 100) : 0;
    const transitions = Math.max(0, total - 1);
    const transitionPercent = count => transitions ? Math.round((count / transitions) * 100) : 0;
    const transitionMatches = matchAudits.filter(match => match.previousOverlap !== 0 || Number(match.matchNo || 0) !== Math.min(...matchAudits.map(item => Number(item.matchNo || 0))));
    const overlapCounts = transitionMatches.reduce((summary, match) => {
      if (match.previousOverlap === 0) summary.none += 1;
      else if (match.previousOverlap === 1) summary.one += 1;
      else if (match.previousOverlap === 2) summary.two += 1;
      else summary.overLimit += 1;
      return summary;
    }, { none: 0, one: 0, two: 0, overLimit: 0 });
    const teammateRelations = matchAudits.flatMap(match => match.teammateDetails);
    const opponentRelations = matchAudits.flatMap(match => match.opponentDetails);
    const playerGames = new Map();
    const pairCounts = new Map();
    matchAudits.forEach(match => {
      getMatchPlayers(match).forEach(player => playerGames.set(player.name, (playerGames.get(player.name) || 0) + 1));
      [...match.teammateDetails.map(item => ({ ...item, type: 'ทีมเดียวกัน' })), ...match.opponentDetails.map(item => ({ ...item, type: 'ตรงข้าม' }))]
        .forEach(item => {
          const key = `${item.type}:${item.label}`;
          pairCounts.set(key, { label: item.label, type: item.type, count: (pairCounts.get(key)?.count || 0) + 1 });
        });
    });
    const playerRows = [...playerGames.entries()].map(([name, games]) => ({ name, games })).sort((left, right) => right.games - left.games || left.name.localeCompare(right.name));
    const gameValues = playerRows.map(player => player.games);
    const reasonCounts = new Map();
    matchAudits.flatMap(match => match.reasons).forEach(reason => reasonCounts.set(reason, (reasonCounts.get(reason) || 0) + 1));
    return {
      total,
      autoAi: matchAudits.filter(match => match.autoAi).length,
      averageScore: total ? Math.round(matchAudits.reduce((sum, match) => sum + match.percent, 0) / total) : 0,
      perfectRate: percent(matchAudits.filter(match => match.percent === 100).length),
      sameRankRate: percent(matchAudits.filter(match => match.metrics.sameRank).length),
      sameIntensityRate: percent(matchAudits.filter(match => match.metrics.sameIntensity).length),
      repeatRuleRate: percent(matchAudits.filter(match => match.metrics.repeatPass).length),
      protectedPairRate: percent(matchAudits.filter(match => match.metrics.protectedPairPass).length),
      anyOverlapChance: transitionPercent(overlapCounts.one + overlapCounts.two + overlapCounts.overLimit),
      overlapCounts,
      transitions,
      teammateRepeatRate: teammateRelations.length ? Math.round((teammateRelations.filter(item => item.count > 1).length / teammateRelations.length) * 100) : 0,
      opponentRepeatRate: opponentRelations.length ? Math.round((opponentRelations.filter(item => item.count > 1).length / opponentRelations.length) * 100) : 0,
      players: playerRows,
      averageGames: playerRows.length ? (gameValues.reduce((sum, value) => sum + value, 0) / playerRows.length).toFixed(1) : '0.0',
      minGames: gameValues.length ? Math.min(...gameValues) : 0,
      maxGames: gameValues.length ? Math.max(...gameValues) : 0,
      topPairs: [...pairCounts.values()].sort((left, right) => right.count - left.count || left.label.localeCompare(right.label)).slice(0, 12),
      reasons: [...reasonCounts.entries()].map(([reason, count]) => ({ reason, count })).sort((left, right) => right.count - left.count)
    };
  }, [matchAudits]);
  const anchor = anchorId === 'AUTO'
    ? [...activePlayers].sort((left, right) => left.games - right.games || left.id.localeCompare(right.id))[0] || null
    : activePlayers.find(player => String(player.id) === String(anchorId)) || null;

  const generatePlayers = () => {
    setData(current => {
      const players = Array.from({ length: generateCount }, (_, index) => {
        const number = current.sequence + index + 1;
        const selectedRank = rank === 'RANDOM' ? RANKS[number % RANKS.length] : rank;
        const selectedIntensity = intensity === 'RANDOM' ? INTENSITIES[number % INTENSITIES.length] : intensity;
        const rankIndex = RANKS.indexOf(selectedRank);
        return {
          id: `test-${number}`,
          name: `ผู้เล่นทดสอบ ${String(number).padStart(3, '0')}`,
          rank: selectedRank,
          intensity: selectedIntensity,
          point: 320 + rankIndex * 48 + (number % 5) * 6,
          games: 0,
          status: 'ACTIVE'
        };
      });
      const logs = players.map(player => ({ id: `${Date.now()}-${player.id}`, playerId: player.id, playerName: player.name, type: 'CREATE', detail: `สร้าง Rank ${player.rank} · ${INTENSITY_META[player.intensity].label}`, at: new Date().toISOString() }));
      return { ...current, sequence: current.sequence + generateCount, players: [...current.players, ...players], logs: [...logs, ...(current.logs || [])].slice(0, 500) };
    });
    setMessage(`สร้างผู้เล่นจำลอง ${generateCount} คนแล้ว`);
  };

  const createPreview = () => {
    if (!anchor) return setMessage('กรุณาเลือกผู้เล่นหลักก่อน');
    const teams = buildAutoMatch(activePlayers, anchor, data.pairs || [], sandboxMatches);
    if (!teams) {
      setPreview(null);
      return setMessage('ยังไม่มีชุดที่ผ่านกฎ: ซ้ำจากเกมก่อนหน้าได้ไม่เกิน 2 คน และคู่เดิมพบกันได้ไม่เกิน 2 รอบ');
    }
    setPreview(teams);
    setMessage(`Preview สำเร็จ · ผลรวมทีมต่างกัน ${teams.gap} คะแนน`);
  };

  const addPreviewToQueue = () => {
    if (!preview) return;
    setData(current => {
      const matchNo = current.matchSequence + 1;
      const ids = [...preview.team1, ...preview.team2].map(player => player.id);
      const queueMatch = { ...preview, id: `local-match-${matchNo}`, matchNo, createdAt: new Date().toISOString() };
      return {
        ...current,
        matchSequence: matchNo,
        players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'QUEUE' } : player),
        queue: [...current.queue, queueMatch],
        logs: [
          ...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-queue`, playerId: id, playerName: player?.name || id, type: 'QUEUE', detail: `เข้า Match #${matchNo}`, at: new Date().toISOString() }; }),
          ...(current.logs || [])
        ].slice(0, 500)
      };
    });
    setPreview(null);
    setAnchorId('AUTO');
    setMessage('เพิ่ม Match เข้า Queue แล้ว');
  };

  const startMatch = matchId => setData(current => {
    const match = current.queue.find(item => item.id === matchId);
    if (!match) return current;
    const ids = [...match.team1, ...match.team2].map(player => player.id);
    return {
      ...current,
      players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'PLAY' } : player),
      queue: current.queue.filter(item => item.id !== matchId),
      playing: [...current.playing, { ...match, startedAt: new Date().toISOString() }],
      logs: [
        ...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-play`, playerId: id, playerName: player?.name || id, type: 'PLAY', detail: `เริ่ม Match #${match.matchNo}`, at: new Date().toISOString() }; }),
        ...(current.logs || [])
      ].slice(0, 500)
    };
  });

  const cancelQueue = matchId => setData(current => {
    const match = current.queue.find(item => item.id === matchId);
    if (!match) return current;
    const ids = [...match.team1, ...match.team2].map(player => player.id);
    return {
      ...current,
      players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'ACTIVE' } : player),
      queue: current.queue.filter(item => item.id !== matchId)
    };
  });

  const finishMatch = (matchId, result) => setData(current => {
    const match = current.playing.find(item => item.id === matchId);
    if (!match) return current;
    const ids = [...match.team1, ...match.team2].map(player => player.id);
    return {
      ...current,
      players: current.players.map(player => ids.includes(player.id)
        ? { ...player, status: 'ACTIVE', games: player.games + 1 }
        : player),
      playing: current.playing.filter(item => item.id !== matchId),
      history: [{ ...match, result, finishedAt: new Date().toISOString() }, ...current.history].slice(0, 50),
      logs: [
        ...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-finish`, playerId: id, playerName: player?.name || id, type: 'FINISH', detail: `จบ Match #${match.matchNo} · ${result}`, at: new Date().toISOString() }; }),
        ...(current.logs || [])
      ].slice(0, 500)
    };
  });

  useEffect(() => {
    if (!autoAiEnabled) return undefined;
    const timer = window.setInterval(() => setData(current => {
      if (current.playing.length > 0) {
        const match = current.playing[0];
        const startedAt = new Date(match.startedAt || 0).getTime();
        if (!match.autoAi || Date.now() - startedAt < 2500) return current;
        const result = ['TEAM_1', 'DRAW', 'TEAM_2'][match.matchNo % 3];
        const ids = [...match.team1, ...match.team2].map(player => player.id);
        return {
          ...current,
          players: current.players.map(player => ids.includes(player.id)
            ? { ...player, status: 'ACTIVE', games: player.games + 1 }
            : player),
          playing: current.playing.slice(1),
          history: [{ ...match, result, finishedAt: new Date().toISOString(), autoAi: true }, ...current.history].slice(0, 50),
          logs: [...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-auto-finish`, playerId: id, playerName: player?.name || id, type: 'FINISH', detail: `Auto AI จบ Match #${match.matchNo} · ${result}`, at: new Date().toISOString() }; }), ...(current.logs || [])].slice(0, 500)
        };
      }
      if (current.queue.length > 0) {
        const match = current.queue[0];
        const ids = [...match.team1, ...match.team2].map(player => player.id);
        return {
          ...current,
          players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'PLAY' } : player),
          queue: current.queue.slice(1),
          playing: [...current.playing, { ...match, startedAt: new Date().toISOString(), autoAi: true }],
          logs: [...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-auto-play`, playerId: id, playerName: player?.name || id, type: 'AUTO_PLAY', detail: `Auto AI เริ่ม Match #${match.matchNo}`, at: new Date().toISOString() }; }), ...(current.logs || [])].slice(0, 500)
        };
      }
      const available = current.players.filter(player => player.status === 'ACTIVE');
      const currentMatches = [...current.history, ...current.queue, ...current.playing];
      const autoMatch = buildAutoMatch(available, null, current.pairs || [], currentMatches);
      if (!autoMatch) return current;
      const matchNo = current.matchSequence + 1;
      const ids = [...autoMatch.team1, ...autoMatch.team2].map(player => player.id);
      const match = { ...autoMatch, id: `local-match-${matchNo}`, matchNo, createdAt: new Date().toISOString(), autoAi: true };
      return {
        ...current,
        matchSequence: matchNo,
        players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'QUEUE' } : player),
        queue: [...current.queue, match],
        logs: [...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-auto-queue`, playerId: id, playerName: player?.name || id, type: 'AUTO_QUEUE', detail: `Auto AI สร้าง Match #${matchNo}`, at: new Date().toISOString() }; }), ...(current.logs || [])].slice(0, 500)
      };
    }), 900);
    return () => window.clearInterval(timer);
  }, [autoAiEnabled]);

  const addProtectedPair = () => {
    if (!pairA || !pairB || pairA === pairB) return setMessage('กรุณาเลือกผู้เล่น 2 คนที่ไม่ซ้ำกัน');
    if ((data.pairs || []).some(pair => [pair.userA, pair.userB].some(id => id === pairA || id === pairB))) return setMessage('ผู้เล่นคนใดคนหนึ่งมีคู่บังคับอยู่แล้ว');
    const playerA = data.players.find(player => player.id === pairA);
    const playerB = data.players.find(player => player.id === pairB);
    setData(current => ({ ...current, pairs: [...(current.pairs || []), { id: pairKey(pairA, pairB), userA: pairA, userB: pairB, nameA: playerA?.name, nameB: playerB?.name, type: pairType }] }));
    setPairA(''); setPairB('');
    setMessage(`เพิ่ม ${pairType === 'LOCKED' ? 'Locked Pair' : 'Waiting Pair'} แล้ว`);
  };

  const removeProtectedPair = pairId => setData(current => ({ ...current, pairs: (current.pairs || []).filter(pair => pair.id !== pairId) }));

  const visibleLogs = (data.logs || []).filter(log => logPlayerId === 'ALL' || log.playerId === logPlayerId);
  const logSummary = visibleLogs.reduce((summary, log) => {
    summary.total += 1;
    if (log.type.includes('QUEUE')) summary.queue += 1;
    if (log.type.includes('PLAY')) summary.play += 1;
    if (log.type === 'FINISH') summary.finish += 1;
    return summary;
  }, { total: 0, queue: 0, play: 0, finish: 0 });

  const resetSandbox = () => {
    if (!window.confirm('ล้างข้อมูลทดสอบทั้งหมดในเครื่องนี้?')) return;
    setData(EMPTY_DATA);
    setPreview(null);
    setAnchorId('AUTO');
    setMessage('ล้างข้อมูลทดสอบแล้ว');
  };

  if (!actor || String(actor.role).toLowerCase() !== 'admin') return null;

  return <main className="admin-shell-with-sidebar poat-shell">
    <AdminSidebar active="pick-one-test" />
    <div className="poat-page">
      <header className="poat-hero"><div><small>LOCAL SANDBOX · ADMIN ONLY</small><h1>ทดสอบระบบ Pick One Auto</h1><p>ข้อมูลทั้งหมดอยู่ใน localStorage ไม่ส่งไป Backend และไม่บันทึก Database</p></div><button type="button" onClick={resetSandbox}>🗑 ล้างข้อมูลทดสอบ</button></header>

      {message && <div className="poat-message" role="status">{message}<button type="button" onClick={() => setMessage('')}>×</button></div>}

      <section className="poat-generator poat-card">
        <div className="poat-title"><span>1</span><div><h2>สร้างผู้เล่นจำลอง</h2><p>เลือกจำนวน Rank และความหนักของผู้เล่นชุดใหม่</p></div><b>{data.players.length} คน</b></div>
        <div className="poat-generator-controls">
          <label><span>จำนวนผู้เล่น</span><select value={generateCount} onChange={event => setGenerateCount(Number(event.target.value))}>{[3,5,7,10,15,20].map(count => <option key={count} value={count}>{count} คน</option>)}</select></label>
          <label><span>ระดับมือ</span><select value={rank} onChange={event => setRank(event.target.value)}><option value="RANDOM">สุ่มทุก Rank</option>{RANKS.map(item => <option key={item} value={item}>Rank {item}</option>)}</select></label>
          <label><span>ความหนัก</span><select value={intensity} onChange={event => setIntensity(event.target.value)}><option value="RANDOM">สุ่มทุกระดับ</option>{INTENSITIES.map(item => <option key={item} value={item}>{INTENSITY_META[item].icon} {INTENSITY_META[item].label}</option>)}</select></label>
          <button type="button" onClick={generatePlayers}>＋ สร้างผู้เล่น {generateCount} คน</button>
        </div>
      </section>

      <section className="poat-builder poat-card">
        <div className="poat-title"><span>2</span><div><h2>Pick One Auto</h2><p>Rank เดียวกันก่อน · ข้าม Rank ใกล้ที่สุด · รักษาคู่บังคับตลอด</p></div><b>{activePlayers.length} ACTIVE</b></div>
        <div className="poat-repeat-rule"><b>กฎป้องกันการพบซ้ำ (เฉพาะโหมดทดสอบ)</b><span>เกมถัดไปใช้ผู้เล่นจากเกมก่อนหน้าได้ไม่เกิน 2 คน</span><span>คู่ทีมเดิมและคู่ตรงข้ามเดิมพบกันได้สูงสุดอย่างละ 2 รอบ</span></div>
        <div className="poat-anchor-row"><label><span>ผู้เล่นหลัก</span><select value={anchorId} onChange={event => { setAnchorId(event.target.value); setPreview(null); }}><option value="AUTO">🤖 Auto เลือกคนเกมน้อยที่สุด</option>{activePlayers.map(player => <option key={player.id} value={player.id}>{player.name} · {player.rank} · {INTENSITY_META[player.intensity].label}</option>)}</select></label><button type="button" onClick={createPreview} disabled={!anchor}>🤖 สร้าง Preview</button></div>
        {preview && <article className="poat-preview"><header><strong>Match Preview</strong><b>Gap {preview.gap} คะแนน</b></header><MatchTeams match={preview} /><button type="button" onClick={addPreviewToQueue}>＋ เพิ่มเข้า Match Queue</button></article>}
      </section>

      <section className="poat-card poat-automation">
        <div className="poat-title"><span>AI</span><div><h2>Auto AI Generate & Play</h2><p>AI เลือกผู้เล่นหลัก สร้าง Queue เริ่มเล่น และจำลองจบ Match เองอัตโนมัติ</p></div><b>{autoAiEnabled ? 'RUNNING' : 'STOPPED'}</b></div>
        <button type="button" className={autoAiEnabled ? 'is-running' : ''} onClick={() => setAutoAiEnabled(value => !value)}>{autoAiEnabled ? '■ หยุด Auto AI' : '▶ เริ่ม Auto AI'}</button>
      </section>

      <section className="poat-card poat-pairs">
        <div className="poat-title"><span>🔗</span><div><h2>Waiting Pair / Locked Pair</h2><p>คู่ที่กำหนดจะถูกเลือกพร้อมกันและอยู่ทีมเดียวกันในทุก Match</p></div><b>{(data.pairs || []).length} คู่</b></div>
        <div className="poat-pair-form"><select value={pairType} onChange={event => setPairType(event.target.value)}><option value="WAITING">Waiting Pair</option><option value="LOCKED">Locked Pair</option></select><select value={pairA} onChange={event => setPairA(event.target.value)}><option value="">ผู้เล่นคนที่ 1</option>{data.players.map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select><select value={pairB} onChange={event => setPairB(event.target.value)}><option value="">ผู้เล่นคนที่ 2</option>{data.players.filter(player => player.id !== pairA).map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select><button type="button" onClick={addProtectedPair}>＋ เพิ่มคู่</button></div>
        <div className="poat-pair-list">{(data.pairs || []).map(pair => <span key={pair.id}><b>{pair.type}</b>{pair.nameA} + {pair.nameB}<button type="button" onClick={() => removeProtectedPair(pair.id)}>×</button></span>)}</div>
      </section>

      <div className="poat-board">
        <section className="poat-card"><div className="poat-title"><span>3</span><div><h2>Match Queue</h2><p>คิวรอเริ่มการแข่งขัน</p></div><b>{data.queue.length} Match</b></div><div className="poat-match-list">{data.queue.length === 0 && <p className="poat-empty">ยังไม่มี Match Queue</p>}{data.queue.map(match => <article className="poat-match" key={match.id}><header><strong>Match #{match.matchNo}</strong><span>WAITING</span></header><MatchTeams match={match} /><footer><button className="start" onClick={() => startMatch(match.id)}>▶ เริ่ม Match</button><button className="cancel" onClick={() => cancelQueue(match.id)}>ยกเลิก</button></footer></article>)}</div></section>
        <section className="poat-card"><div className="poat-title"><span>4</span><div><h2>Match Playing</h2><p>เลือกผลเพื่อจบและบันทึกประวัติ</p></div><b>{data.playing.length} Match</b></div><div className="poat-match-list">{data.playing.length === 0 && <p className="poat-empty">ยังไม่มี Match กำลังเล่น</p>}{data.playing.map(match => <article className="poat-match is-playing" key={match.id}><header><strong>Match #{match.matchNo}</strong><span>PLAYING</span></header><MatchTeams match={match} /><footer className="results"><button onClick={() => finishMatch(match.id, 'TEAM_1')}>🏆 T1 ชนะ</button><button onClick={() => finishMatch(match.id, 'DRAW')}>🤝 เสมอ</button><button onClick={() => finishMatch(match.id, 'TEAM_2')}>🏆 T2 ชนะ</button></footer></article>)}</div></section>
      </div>

      <section className="poat-card poat-history"><div className="poat-title"><span>5</span><div><h2>ประวัติที่จบแล้ว</h2><p>เก็บล่าสุด 50 Match ใน localStorage</p></div><b>{data.history.length} Match</b></div>{data.history.length === 0 ? <p className="poat-empty">ยังไม่มีประวัติ</p> : <div>{data.history.map(match => <article key={`${match.id}-${match.finishedAt}`}><b>Match #{match.matchNo}</b><span>{match.team1.map(player => player.name).join(' + ')} vs {match.team2.map(player => player.name).join(' + ')}</span><strong>{match.result === 'TEAM_1' ? 'T1 ชนะ' : match.result === 'TEAM_2' ? 'T2 ชนะ' : 'เสมอ'}</strong></article>)}</div>}</section>

      <section className="poat-card poat-audit">
        <div className="poat-title"><span>%</span><div><h2>ผลทดสอบเงื่อนไขการจับคู่</h2><p>ตรวจ Rank · ความหนัก · คนซ้ำจากเกมก่อน · จำนวนครั้งที่พบกัน · คู่บังคับ</p></div><b>{auditPercent}% ตรงเงื่อนไข</b></div>
        {matchAudits.length === 0 ? <p className="poat-empty">สร้าง Match เพื่อเริ่มเก็บผลทดสอบ</p> : <div className="poat-audit-list">{matchAudits.map(match => <article key={`audit-${match.id}-${match.matchNo}`}>
          <header><strong>Match #{match.matchNo}</strong><span className={match.percent === 100 ? 'is-perfect' : ''}>{match.percent}%</span></header>
          <div className="poat-audit-teams"><AuditTeam label="Team 1" players={match.team1} /><em>VS</em><AuditTeam label="Team 2" players={match.team2} /></div>
          <div className="poat-audit-relations"><span>ทีมเดียวกัน</span>{match.teammateDetails.map(item => <small key={item.label}>{item.label} ({item.count})</small>)}</div>
          <div className="poat-audit-relations opponents"><span>ตรงข้าม</span>{match.opponentDetails.map(item => <small key={item.label}>{item.label} ({item.count})</small>)}</div>
          {match.reasons.length > 0 ? <div className="poat-audit-reasons"><b>สาเหตุที่ไม่ได้ 100%</b>{match.reasons.map(reason => <span key={reason}>• {reason}</span>)}</div> : <div className="poat-audit-perfect">✓ ผ่านครบทุกเงื่อนไข</div>}
        </article>)}</div>}
      </section>

      <section className="poat-card poat-overall-report">
        <div className="poat-title"><span>📊</span><div><h2>Report ภาพรวมจาก Match ที่ Random</h2><p>สถิติจากผลทดลองจริงใน Sandbox ค่ายิ่งมีจำนวน Match มากยิ่งสะท้อนแนวโน้มได้ดี</p></div><b>{overallReport.total} Match</b></div>
        {overallReport.total === 0 ? <p className="poat-empty">ยังไม่มีข้อมูลเพียงพอสำหรับออกรายงาน</p> : <>
          <div className="poat-report-kpis">
            <span><small>คะแนนเงื่อนไขเฉลี่ย</small><b>{overallReport.averageScore}%</b></span>
            <span><small>Match ที่ได้ 100%</small><b>{overallReport.perfectRate}%</b></span>
            <span><small>Rank เดียวกันครบ 4 คน</small><b>{overallReport.sameRankRate}%</b></span>
            <span><small>ความหนักระดับเดียวกัน</small><b>{overallReport.sameIntensityRate}%</b></span>
            <span><small>ผ่านกฎไม่เจอซ้ำเกิน 2</small><b>{overallReport.repeatRuleRate}%</b></span>
            <span><small>Waiting/Locked Pair ถูกต้อง</small><b>{overallReport.protectedPairRate}%</b></span>
          </div>

          <div className="poat-report-highlight">
            <div><small>โอกาสพบผู้เล่นจากเกมก่อนหน้าซ้ำอย่างน้อย 1 คน</small><strong>{overallReport.anyOverlapChance}%</strong><p>เป็นอัตราที่เกิดขึ้นจริงจาก {overallReport.transitions} การเปลี่ยนเกม ไม่ใช่ค่าคาดการณ์ตายตัว</p></div>
            <ul>
              <li>ไม่ซ้ำเลย <b>{overallReport.overlapCounts.none}</b> เกม</li>
              <li>ซ้ำ 1 คน <b>{overallReport.overlapCounts.one}</b> เกม</li>
              <li>ซ้ำ 2 คน <b>{overallReport.overlapCounts.two}</b> เกม</li>
              <li className={overallReport.overlapCounts.overLimit ? 'is-warning' : ''}>เกิน 2 คน <b>{overallReport.overlapCounts.overLimit}</b> เกม</li>
            </ul>
          </div>

          <div className="poat-report-grid">
            <article><h3>การพบคู่เดิมซ้ำ</h3><p>ช่องความสัมพันธ์ที่เคยเกิดมาแล้วอย่างน้อยหนึ่งครั้ง</p><div className="poat-report-bars"><span><i style={{ width: `${overallReport.teammateRepeatRate}%` }} />คู่ทีมเดิม <b>{overallReport.teammateRepeatRate}%</b></span><span><i style={{ width: `${overallReport.opponentRepeatRate}%` }} />คู่แข่งเดิม <b>{overallReport.opponentRepeatRate}%</b></span></div></article>
            <article><h3>ความทั่วถึงของผู้เล่น</h3><p>ผู้เล่นที่ได้ลงทั้งหมด {overallReport.players.length} คน</p><div className="poat-report-fairness"><span>เฉลี่ย <b>{overallReport.averageGames}</b> เกม/คน</span><span>น้อยสุด <b>{overallReport.minGames}</b></span><span>มากสุด <b>{overallReport.maxGames}</b></span></div></article>
          </div>

          <div className="poat-report-grid">
            <article><h3>คู่ที่พบกันบ่อยที่สุด</h3><div className="poat-report-table">{overallReport.topPairs.length === 0 ? <p>ยังไม่มีข้อมูล</p> : overallReport.topPairs.map(item => <span key={`${item.type}-${item.label}`}><b>{item.label}</b><small>{item.type}</small><strong>({item.count})</strong></span>)}</div></article>
            <article><h3>สาเหตุที่ Match ไม่ได้ 100%</h3><div className="poat-report-table reasons">{overallReport.reasons.length === 0 ? <p className="is-success">✓ ทุก Match ผ่านครบทุกเงื่อนไข</p> : overallReport.reasons.map(item => <span key={item.reason}><b>{item.reason}</b><strong>{item.count} Match</strong></span>)}</div></article>
          </div>

          <article className="poat-player-distribution"><h3>จำนวนครั้งที่ผู้เล่นแต่ละคนได้ลง</h3><div>{overallReport.players.map(player => <span key={player.name}><b>{player.name}</b><strong>{player.games} เกม</strong></span>)}</div></article>
          <p className="poat-report-note">สรุปจากทั้งหมด {overallReport.total} Match: Auto AI {overallReport.autoAi} Match และสร้างด้วยมือ {overallReport.total - overallReport.autoAi} Match — เปอร์เซ็นต์จะเปลี่ยนตามจำนวนผู้เล่น Rank ความหนัก และประวัติที่สุ่มเพิ่มเข้ามา</p>
        </>}
      </section>

      <section className="poat-card poat-logs"><div className="poat-title"><span>LOG</span><div><h2>สรุป Log รายบุคคล</h2><p>ตรวจสอบการสร้าง เข้า Queue เริ่มเล่น และจบ Match ของแต่ละคน</p></div><b>{visibleLogs.length} รายการ</b></div><label><span>กรองผู้เล่น</span><select value={logPlayerId} onChange={event => setLogPlayerId(event.target.value)}><option value="ALL">ผู้เล่นทั้งหมด</option>{data.players.map(player => <option key={player.id} value={player.id}>{player.name} · {player.games} เกม</option>)}</select></label><div className="poat-log-summary"><span>ทั้งหมด <b>{logSummary.total}</b></span><span>เข้า Queue <b>{logSummary.queue}</b></span><span>เริ่มเล่น <b>{logSummary.play}</b></span><span>จบ Match <b>{logSummary.finish}</b></span></div><div className="poat-log-list">{visibleLogs.length === 0 ? <p className="poat-empty">ยังไม่มี Log</p> : visibleLogs.map(log => <article key={log.id}><time>{new Date(log.at).toLocaleString('th-TH')}</time><b>{log.playerName}</b><strong>{log.type}</strong><span>{log.detail}</span></article>)}</div></section>
    </div>
  </main>;
}
